// SENTINEL: 5_Cognitive - Chat Interface (Messaging, CodeBlocks, Thoughts)
// Vitality: Healthy (Pass 10 Polish)
import React, { useState, useRef, useEffect, useMemo } from 'react';
import { Message, MessageRole, Idea, ProposalChange, ExecutionPlan } from '../types';
import { Send, Search, CheckCircle, XCircle, Loader2, Mic, MicOff, Bot, User, Globe, Sparkles, X, ChevronDown, ChevronUp, PlusCircle, RefreshCw, Trash2, Edit2, Check, ArrowRight, Layers, CheckSquare, XSquare, ListChecks, ArrowRightCircle, Rocket, Play, BrainCircuit, ChevronRight, Zap, Component, Info, FileText, ExternalLink, BookOpen, SkipForward, Settings2, PanelRightOpen, PanelRightClose, ChevronLeft, Maximize2, Minimize2, MessageSquare, Copy, ArrowDownCircle, Pin, Cloud } from 'lucide-react';
import { CodeBlock } from './CodeBlock';
import { AVATAR_STYLES } from './avatarStyles';
import ReactMarkdown from 'react-markdown';
import RemarkGfm from 'remark-gfm';
// import Collapsible from 'react-collapsible'; // Removed due to React 19 incompatibility
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { atomDark } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useSmoothScroll } from '../hooks/useSmoothScroll';
import { logger } from '../services/loggingService';

const formatModelName = (model: string): string => {
    const modelMap: Record<string, string> = {
        'gemini-2.5-pro': 'Gemini 2.5 Pro',
        'gemini-2.5-flash': 'Gemini 2.5 Flash',
        'gemini-2.0-flash': 'Gemini 2.0 Flash',
    };
    return modelMap[model] || model.replace('gemini-', 'Gemini ').replace(/-/g, ' ');
};

interface ChatInterfaceProps {
    messages: Message[];
    activeIdea: Idea | null;
    currentPlan: ExecutionPlan | null;
    onSendMessage: (text: string) => void;
    onAcceptProposalChange: (messageId: string, change: ProposalChange) => void;
    onRejectProposalChange: (messageId: string, changeId: string) => void;
    onDeleteMessage?: (messageId: string) => void;
    onRequestRevision: (messageId: string, changeId: string, feedback: string) => void;
    onAcceptAllChanges?: (messageId: string, changes: ProposalChange[]) => void;
    onRejectAllChanges?: (messageId: string, changes: ProposalChange[]) => void;
    isProcessing: boolean;
    onStartLiveMode: () => void;

    // New: Local Voice Mode
    isLocalMode?: boolean;
    onStartLocalVoiceMode?: () => void;

    isArchitectMode: boolean;
    setIsArchitectMode: (enabled: boolean) => void;
    creativityLevel?: number;
    setCreativityLevel?: (level: number) => void;
    hideHeader?: boolean;
    isLiveMode?: boolean;
    liveStatus?: 'connecting' | 'connected' | 'error' | 'idle';
    isMicOn?: boolean;
    setIsMicOn?: (on: boolean) => void;
    isAudioSkipped?: boolean;
    isAudioPlaying?: boolean;
    isWaitingForResponse?: boolean;
    streamingModelText?: string;
    selectedAvatar?: string;
    onToggleAudioSkip?: () => void;
    showControlsBar?: boolean;
    setShowControlsBar?: (show: boolean) => void;
    isChatFullscreen?: boolean;
    setIsChatFullscreen?: (fullscreen: boolean) => void;
    isChatCollapsed?: boolean;
    setIsChatCollapsed?: (collapsed: boolean) => void;
    theme?: 'light' | 'dark';
    showCompactSearch?: boolean;
    modelContextLength?: number;
    activeProvider?: string;
    onToggleProvider?: () => void;
}

// Helper component for the Thinking Block
const ThinkingBlock: React.FC<{ content: string; label: string; isStreaming?: boolean }> = ({ content, label, isStreaming }) => {
    // Default to collapsed to reduce noise, as requested
    const [isExpanded, setIsExpanded] = useState(false);

    return (
        <div className="mb-4 rounded-xl border border-cc-border-muted bg-cc-surface/50 overflow-hidden text-sm">
            <button
                onClick={() => setIsExpanded(!isExpanded)}
                className="w-full flex items-center gap-2 px-3 py-2 bg-cc-accent-muted/10 hover:bg-cc-accent-muted/20 transition-colors text-left"
            >
                <div className={`p-1 rounded-md ${isStreaming ? 'bg-cc-accent/10 animate-pulse' : 'bg-cc-border-muted/30'}`}>
                    <BrainCircuit size={14} className={`text-cc-accent ${isStreaming ? "animate-pulse" : ""}`} />
                </div>
                <span className="text-xs font-semibold text-cc-text-muted flex-1">
                    {label || "Thinking Process"}
                </span>
                <span className="text-[10px] text-cc-text-subtle mr-2">
                    {isExpanded ? 'Hide' : 'Show'}
                </span>
                {isExpanded ? <ChevronDown size={14} className="text-cc-text-muted" /> : <ChevronRight size={14} className="text-cc-text-muted" />}
            </button>

            {isExpanded && (
                <div className="p-3 border-t border-cc-border-muted bg-cc-accent-muted/5 animate-in slide-in-from-top-1 duration-200">
                    <div className="text-xs text-cc-text-muted font-mono leading-relaxed max-h-[300px] overflow-y-auto custom-scrollbar pr-2 select-text">
                        <ReactMarkdown>{content}</ReactMarkdown>
                        {isStreaming && (
                            <span className="inline-block w-1.5 h-3 ml-1 bg-cc-accent animate-pulse align-middle" />
                        )}
                    </div>
                </div>
            )}
        </div>
    );
};

const CopyButton: React.FC<{ text: string; className?: string; sourceId?: string; variant?: string; size?: string; title?: string }> = ({ text, className, sourceId, title, size }) => {
    const [copied, setCopied] = useState(false);

    const handleCopy = async (e: React.MouseEvent) => {
        e.stopPropagation();
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);

        try {
            // If we have a source ID, try to copy as HTML rich text
            if (sourceId) {
                const element = document.getElementById(sourceId);
                if (element) {
                    const html = element.innerHTML;
                    const blobHtml = new Blob([html], { type: 'text/html' });
                    const blobText = new Blob([text], { type: 'text/plain' });
                    const data = [new ClipboardItem({
                        'text/html': blobHtml,
                        'text/plain': blobText
                    })];
                    await navigator.clipboard.write(data);
                    return;
                }
            }
            // Fallback to plain text
            await navigator.clipboard.writeText(text);
        } catch (err) {
            console.error('Copy failed, falling back to text', err);
            navigator.clipboard.writeText(text);
        }
    };

    return (
        <button
            onClick={handleCopy}
            className={`rounded-lg transition-all flex items-center justify-center ${size === 'xs' ? 'p-1' : 'p-1.5'} ${copied ? 'text-emerald-500 bg-emerald-50 dark:bg-emerald-900/20' : 'text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile-hover'} ${className || ''}`}
            title={title || (copied ? "Copied!" : "Copy to clipboard")}
        >
            {copied ? <Check size={size === 'xs' ? 12 : 14} /> : <Copy size={size === 'xs' ? 12 : 14} />}
        </button>
    );
};

// Reusable Sources Component
const SourcesDisplay = ({ sources }: { sources: { uri: string; title: string }[] }) => {
    if (!sources || sources.length === 0) return null;

    // Quick favicon helper (Google S2 is standard/reliable)
    const getFavicon = (url: string) => {
        try {
            return `https://www.google.com/s2/favicons?domain=${new URL(url).hostname}`;
        } catch {
            return '';
        }
    };

    return (
        <div className="border-t border-cc-border-muted/50 pt-3 mt-2 px-5 mb-4">
            <div className="flex items-center gap-2 mb-2 text-[10px] uppercase tracking-wider font-bold text-cc-text-muted/70">
                <Globe size={11} />
                <span>Sources & Citations</span>
            </div>
            <div className="flex flex-wrap gap-2">
                {sources.map((source, idx) => (
                    <a
                        key={idx}
                        href={source.uri}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-2 bg-cc-surface hover:bg-cc-tile border border-cc-border hover:border-cc-accent/30 rounded-lg px-2.5 py-1.5 transition-all group max-w-[200px]"
                        title={source.title}
                    >
                        <img src={getFavicon(source.uri)} className="w-3 h-3 opacity-60 group-hover:opacity-100" alt="" onError={(e) => e.currentTarget.style.display = 'none'} />
                        <span className="text-xs text-cc-text-muted group-hover:text-cc-text truncate">{source.title}</span>
                    </a>
                ))}
            </div>
        </div>
    );
};

// Briefing Card - Voice-delegated search results
const BriefingCard: React.FC<{
    // ... existing briefing card code ...

    query: string;
    content: string;
    sources: { uri: string; title: string }[];
}> = ({ query, content, sources }) => {
    const [isExpanded, setIsExpanded] = useState(true);

    return (
        <div className="rounded-xl overflow-hidden border-2 border-cc-accent/30 bg-cc-accent-muted shadow-lg animate-in fade-in slide-in-from-bottom-2">
            {/* Header */}
            <div className="px-5 py-3 bg-cc-accent/10 border-b border-cc-accent/20">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <div className="p-1.5 bg-cc-accent rounded-lg">
                            <FileText size={14} className="text-cc-accent-text" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-cc-text">Research Briefing</h3>
                            <p className="text-[10px] text-cc-accent truncate max-w-[300px]">"{query}"</p>
                        </div>
                    </div>
                    <button
                        onClick={() => setIsExpanded(!isExpanded)}
                        className="p-1 hover:bg-cc-accent/20 rounded transition-colors"
                    >
                        {isExpanded ? <ChevronDown size={16} className="text-cc-accent" /> : <ChevronRight size={16} className="text-cc-accent" />}
                    </button>
                </div>
            </div>

            {isExpanded && (
                <>
                    {/* Content */}
                    <div className="px-5 py-4 markdown-content text-cc-text">
                        <ReactMarkdown
                            remarkPlugins={[RemarkGfm]}
                            components={{
                                a: ({ node, ...props }) => <a {...props} className="text-cc-accent hover:underline" target="_blank" />,
                                h1: ({ node, ...props }) => <h1 {...props} className="text-lg font-bold mb-2 mt-3 text-cc-text" />,
                                h2: ({ node, ...props }) => <h2 {...props} className="text-md font-bold mb-2 mt-3 text-cc-text" />,
                                h3: ({ node, ...props }) => <h3 {...props} className="text-sm font-semibold mb-1 mt-2 text-cc-accent" />,
                                ul: ({ node, ...props }) => <ul {...props} className="list-disc list-outside ml-4 mb-3 space-y-1" />,
                                ol: ({ node, ...props }) => <ol {...props} className="list-decimal list-outside ml-4 mb-3 space-y-1" />,
                                code({ node, inline, className, children, ...props }: any) {
                                    return inline ? (
                                        <code className="bg-cc-accent/10 text-cc-accent px-1.5 py-0.5 rounded text-xs font-mono" {...props}>
                                            {children}
                                        </code>
                                    ) : (
                                        <pre className="bg-slate-900 text-emerald-400 p-3 rounded-lg overflow-x-auto my-2 text-xs font-mono" {...props}>
                                            <code>{children}</code>
                                        </pre>
                                    )
                                }
                            }}
                        >
                            {content}
                        </ReactMarkdown>
                    </div>

                    {/* Sources */}
                    {sources.length > 0 && (
                        <div className="px-5 py-3 bg-cc-surface border-t border-cc-border">
                            <p className="text-[10px] font-bold text-cc-accent mb-2 uppercase tracking-wider flex items-center gap-1.5">
                                <Globe size={11} /> Sources
                            </p>
                            <div className="flex flex-wrap gap-2">
                                {sources.map((source, idx) => (
                                    <a
                                        key={idx}
                                        href={source.uri}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-xs bg-cc-elevated text-cc-accent px-2.5 py-1.5 rounded-lg hover:bg-cc-tile-hover border border-cc-border transition-colors flex items-center gap-1.5 shadow-sm"
                                    >
                                        <ExternalLink size={10} />
                                        <span className="truncate max-w-[140px]">{source.title}</span>
                                    </a>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Footer hint */}
                    <div className="px-5 py-2 bg-cc-surface/50 border-t border-cc-border/50">
                        <p className="text-[10px] text-cc-text-muted italic text-center">
                            Say "I've reviewed this" to continue the conversation
                        </p>
                    </div>
                </>
            )}
        </div>
    );
};

// Helper for performance classes
const getPerformanceClass = (level = 1) => {
    switch (level) {
        case 1: return 'will-change-transform'; // Minimal
        case 2: return 'will-change-transform translate-z-0'; // Moderate
        case 3: return 'will-change-transform translate-z-0 contain-content'; // Aggressive (use for list items)
        default: return '';
    }
};

// Helper function to parse message content (moved to module scope)
const parseMessageContent = (text: string) => {
    if (!text) return { thinking: null, cleanText: '' };

    let thinking = null;
    let cleanText = text;
    let isStreamingThinking = false;

    // --- ROBUST THINKING PARSING ---

    // 1. Standard Tags (Highest Priority)
    const completeMatch = text.match(/<thinking>([\s\S]*?)<\/thinking>/i) ||
        text.match(/<think>([\s\S]*?)<\/think>/i);

    if (completeMatch) {
        thinking = completeMatch[1].trim();
        cleanText = text
            .replace(/<thinking>[\s\S]*?<\/thinking>/gi, '')
            .replace(/<think>[\s\S]*?<\/think>/gi, '')
            .trim();
    } else {
        // 2. Streaming Tags (Unclosed)
        const openMatch = text.match(/([\s\S]*?)<thinking>([\s\S]*)$/i) ||
            text.match(/([\s\S]*?)<think>([\s\S]*)$/i);

        if (openMatch) {
            // content before <think> is usually nothing, or maybe a "Sure!"
            const preText = openMatch[1];
            thinking = openMatch[2].trim();
            cleanText = preText.trim();
            isStreamingThinking = true;
        } else {
            // 3. Specific Headers (Internal Reasoning, etc.) - Priority over generic heuristics
            // Match explicit separator format first (High Confidence)
            const explicitSeparatorMatch = text.match(/^(?:Thinking Process|Internal Reasoning|Thinking|Reasoning)[:\s\n]*([\s\S]+?)\n\s*[=\-]{3,}\s*(?:FINAL ANSWER)?\s*={3,}/i);

            if (explicitSeparatorMatch) {
                thinking = explicitSeparatorMatch[1].trim();
                cleanText = text.substring(explicitSeparatorMatch[0].length).trim();
            } else {
                // 4. Heuristic for Untagged Thinking (Common in local/ONNX models)
                // Pattern: Starts with internal monologue/planning, transitions to answer.
                // Looking for "User wants...", "Okay, let's...", "I need to..." followed by a separator or "Here is..."

                // This regex looks for a block at the START that seems like reasoning
                // It stops when it hits a double newline followed by a "Transition Phrase"
                // Added "Wait, the user" and "I can" and other common starts to the regex
                const untaggedPattern = /^[\s\r\n]*((?:The user|User|Okay|Ok|So|First|I need|To answer|Let's|Lets|I will|Wait, the user)[\s\S]+?)(?=\n\n(Here|Certainly|Sure|The|Below|1\.|#|Accordingly|Therefore|I can|My capabilities|Base|To|In summary))/i;

                // Or specific "DeepSeek" style chain of thought ending in \boxed or [Final]
                const boxedMatch = text.match(/([\s\S]+?)(\\boxed\{)/);

                if (boxedMatch) {
                    // Everything before \boxed is thinking
                    thinking = boxedMatch[1].trim();
                    const boxedPart = text.substring(boxedMatch[1].length).trim();
                    cleanText = boxedPart;
                } else if (untaggedPattern.test(text)) { // Check regex before executing match for cleaner logic
                    const heuristicMatch = text.match(untaggedPattern);
                    // Only apply heuristic if specific keywords exist and it's long enough to be a thought
                    if (heuristicMatch && heuristicMatch[1].length > 40) {
                        thinking = heuristicMatch[1].trim();
                        cleanText = text.substring(heuristicMatch[0].length).trim();
                    }
                }
            }
        }
    }

    // fallback: if we have thinking but no clean text (and not streaming), show thinking as text? 
    // No, keep it collapsed.

    // Also clean <actions> if the service missed it (safety)
    cleanText = cleanText.replace(/<actions>[\s\S]*?<\/actions>/g, '').trim();

    return { thinking, cleanText, isStreamingThinking };
};

// Simple replacement for react-collapsible to avoid React 19 peer dependency issues
const SimpleCollapsible: React.FC<{
    trigger: React.ReactNode;
    triggerWhenOpen?: React.ReactNode;
    open?: boolean;
    children: React.ReactNode;
}> = ({ trigger, triggerWhenOpen, open, children }) => {
    const [isOpen, setIsOpen] = useState(open || false);

    // Sync with external open prop changes (e.g. streaming started/stopped)
    useEffect(() => {
        if (open !== undefined) setIsOpen(open);
    }, [open]);

    return (
        <div>
            <div onClick={() => setIsOpen(!isOpen)} className="cursor-pointer select-none">
                {isOpen && triggerWhenOpen ? triggerWhenOpen : trigger}
            </div>
            <div className={`transition-all duration-300 overflow-hidden ${isOpen ? 'max-h-[1000px] opacity-100' : 'max-h-0 opacity-0'}`}>
                {children}
            </div>
        </div>
    );
};

// Extracted ChatMessage component to prevent list re-renders on new messages
const ChatMessage = React.memo(({
    msg,
    isLiveMode,
    activeIdea,
    activeRevisionId,
    revisionInputs,
    setActiveRevisionId,
    setRevisionInputs,
    handleSubmitRevision,
    onRejectProposalChange,
    onAcceptProposalChange,
    onAcceptAllChanges,
    onRejectAllChanges,
    onSendMessage,
    onDeleteMessage,
    theme
}: any) => {
    // Parse thinking blocks if present
    const { thinking, cleanText, isStreamingThinking } = parseMessageContent(msg.text);

    // Copy handlers
    // const handleCopyText = () => handleCopy(cleanText, `msg-${msg.id}`);
    // const handleCopyHTML = () => handleCopy(cleanText, `msg-${msg.id}`, true);

    return (
        <div
            id={`msg-${msg.id}`}
            className={`group flex flex-col gap-1 p-4 sm:p-6 mb-4 relative rounded-2xl border transition-all duration-300 ${msg.sender === 'user'
                ? 'bg-cc-surface border-cc-border ml-8 sm:ml-12 shadow-sm'
                : 'bg-cc-elevated border-cc-border mr-8 sm:mr-12 shadow-md'
                } ${getPerformanceClass(3)}`}
        >
            {/* Thinking Block with Auto-Collapse */}
            {(thinking || isStreamingThinking) && msg.sender === 'ai' && (
                <div className="mb-3">
                    <SimpleCollapsible
                        trigger={
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cc-text-muted hover:text-cc-accent cursor-pointer transition-colors w-fit select-none">
                                <BrainCircuit size={14} className={isStreamingThinking ? "animate-pulse text-cc-accent" : ""} />
                                <span>{isStreamingThinking ? "Processing Thought Process..." : "Reasoning Process"}</span>
                                {isStreamingThinking && <span className="w-1.5 h-1.5 rounded-full bg-cc-accent animate-ping" />}
                            </div>
                        }
                        // Default to open if streaming, closed if done
                        open={isStreamingThinking}
                        triggerWhenOpen={
                            <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-cc-accent cursor-pointer transition-colors w-fit select-none mb-1">
                                <BrainCircuit size={14} />
                                <span>Hide Reasoning</span>
                            </div>
                        }
                    >
                        <div className="pl-3 border-l-2 border-cc-border mt-1">
                            <div className="text-xs sm:text-sm text-cc-text-muted italic font-mono leading-relaxed bg-cc-surface/50 p-3 rounded-lg border border-cc-border/50 overflow-x-auto w-full">
                                <ReactMarkdown
                                    remarkPlugins={[RemarkGfm]}
                                    components={{
                                        p: ({ children }) => <p className="mb-2 last:mb-0 inline-block w-full">{children}</p>,
                                        code: ({ inline, className, children, ...props }: any) => {
                                            if (inline) {
                                                return <code className="bg-cc-tile px-1 py-0.5 rounded text-xs not-italic" {...props}>{children}</code>;
                                            }
                                            return <code className="block bg-cc-tile p-2 rounded-lg text-xs not-italic my-2 whitespace-pre-wrap overflow-x-auto" {...props}>{children}</code>;
                                        }
                                    }}
                                >
                                    {thinking}
                                </ReactMarkdown>
                                {isStreamingThinking && <span className="inline-block w-1.5 h-3 ml-1 bg-cc-accent animate-pulse align-middle" />}
                            </div>
                        </div>
                    </SimpleCollapsible>
                </div>
            )}

            {/* Sender Label & Timestamp */}
            <div className="flex items-center justify-between mb-1 opacity-60 text-[10px] uppercase tracking-wider font-bold text-cc-text-muted">
                <span className="flex items-center gap-1.5">
                    {msg.sender === 'user' ? 'You' : (activeIdea?.title || 'Assistant')}
                </span>
                <span>{new Date(msg.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
            </div>

            {/* Top Toolbar (Copy, etc) - visible on hover */}
            <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1">
                <CopyButton text={cleanText} sourceId={`msg-content-${msg.id}`} variant="ghost" size="xs" />
                {onDeleteMessage && (
                    <button
                        onClick={() => onDeleteMessage(msg.id)}
                        className="p-1 text-cc-text-muted hover:text-red-500 hover:bg-red-500/10 rounded transition-colors"
                        title="Delete Message"
                    >
                        <Trash2 size={14} />
                    </button>
                )}
            </div>

            {/* Message Content */}
            <div id={`msg-content-${msg.id}`} className={`prose prose-sm dark:prose-invert max-w-none text-cc-text leading-relaxed break-words
                ${msg.sender === 'user' ? 'prose-p:text-cc-text-subtle' : ''}
                prose-headings:font-display prose-headings:font-bold prose-headings:text-cc-text
                prose-a:text-cc-accent prose-a:no-underline hover:prose-a:underline
                prose-code:text-cc-accent prose-code:bg-cc-tile prose-code:px-1 prose-code:py-0.5 prose-code:rounded-md prose-code:before:content-none prose-code:after:content-none
                prose-pre:bg-cc-base prose-pre:border prose-pre:border-cc-border prose-pre:shadow-sm prose-pre:rounded-xl
                prose-strong:text-cc-text prose-strong:font-bold
                prose-ul:list-disc prose-ul:pl-4
                prose-ol:list-decimal prose-ol:pl-4
                prose-img:rounded-xl prose-img:shadow-md
                prose-table:border-collapse prose-table:w-full prose-table:my-4 prose-table:rounded-lg prose-table:overflow-hidden prose-table:shadow-sm prose-table:border prose-table:border-cc-border
                prose-thead:bg-cc-tile prose-thead:text-cc-text
                prose-th:p-3 prose-th:text-left prose-th:font-bold prose-th:border-b prose-th:border-cc-border
                prose-td:p-3 prose-td:border-b prose-td:border-cc-border last:prose-td:border-0
                [&>table]:!my-0 [&>table]:!w-full [&>table]:!border-collapse [&>table]:!border [&>table]:!border-cc-border [&>table]:!rounded-lg [&>table]:!shadow-sm [&>table]:!overflow-hidden
            `}>
                <ReactMarkdown
                    remarkPlugins={[RemarkGfm]}
                    components={{
                        // @ts-ignore
                        code({ node, inline, className, children, ...props }: any) {
                            const match = /language-(\w+)/.exec(className || '');
                            if (!inline && match) {
                                return (
                                    <CodeBlock
                                        language={match[1]}
                                        code={String(children).replace(/\n$/, '')}
                                        theme={theme}
                                    />
                                );
                            }
                            return (
                                <code className={className} {...props}>
                                    {children}
                                </code>
                            );
                        },
                        table: ({ children }) => (
                            <div className="overflow-x-auto my-4 rounded-xl border border-cc-border bg-cc-elevated shadow-sm">
                                <table className="w-full text-sm text-left">{children}</table>
                            </div>
                        ),
                        thead: ({ children }) => <thead className="bg-cc-tile border-b border-cc-border text-xs uppercase font-bold text-cc-text-muted">{children}</thead>,
                        th: ({ children }) => <th className="px-4 py-3 whitespace-nowrap bg-cc-surface/50 backdrop-blur-sm sticky top-0">{children}</th>,
                        td: ({ children }) => <td className="px-4 py-3 border-b border-cc-border last:border-0 hover:bg-cc-tile/30 transition-colors">{children}</td>,
                        tr: ({ children }) => <tr className="hover:bg-cc-tile/20 transition-colors even:bg-cc-surface/30">{children}</tr>,
                    }}
                >
                    {cleanText}
                </ReactMarkdown>

                {/* Bottom Actions Row (Copy HTML, Resend) */}
                <div className="flex items-center gap-2 mt-2 opacity-0 group-hover:opacity-100 transition-opacity">
                    <CopyButton text={cleanText} sourceId={`msg-content-${msg.id}`} title="Copy Text" />
                    <button
                        className="p-1.5 text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile rounded-lg transition-all"
                        title="Regenerate"
                        onClick={() => onSendMessage(msg.text, true)} // Resend as retry
                    >
                        <RefreshCw size={14} />
                    </button>
                    {/* Add more message actions here */}
                </div>
            </div>

            {/* Proposal Rendering Logic (if present) */}
            {msg.proposal && (
                <div className="mt-4 bg-cc-surface rounded-xl border border-cc-border overflow-hidden shadow-sm">
                    <div className="p-3 bg-cc-tile border-b border-cc-border flex items-center justify-between">
                        <div className="flex items-center gap-2">
                            <FileText size={16} className="text-cc-accent" />
                            <span className="text-xs font-bold uppercase tracking-wider text-cc-text">Proposed Changes</span>
                        </div>
                        <div className="flex items-center gap-2">
                            {(msg.proposal?.changes?.length ?? 0) > 1 && (
                                <>
                                    <button
                                        onClick={() => {
                                            logger.info('Chat', 'Proposal Rejected (All)', { messageId: msg.id });
                                            onRejectAllChanges(msg.id);
                                        }}
                                        className="px-2 py-1 text-[10px] font-bold text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                    >
                                        Reject All
                                    </button>
                                    <button
                                        onClick={() => {
                                            logger.info('Chat', 'Proposal Accepted (All)', { messageId: msg.id, changesCount: msg.proposal?.changes?.length });
                                            onAcceptAllChanges(msg.id);
                                        }}
                                        className="px-2 py-1 text-[10px] font-bold text-emerald-600 hover:bg-emerald-50 dark:hover:bg-emerald-900/20 rounded-lg transition-colors"
                                    >
                                        Accept All
                                    </button>
                                </>
                            )}
                        </div>
                    </div>

                    {/* Collapsible Changes List */}
                    <div className="divide-y divide-cc-border max-h-[400px] overflow-y-auto custom-scrollbar">
                        {msg.proposal.changes.map((change: any, idx: number) => (
                            <div key={idx} className="p-3 hover:bg-cc-tile/30 transition-colors group/change">
                                <div className="flex items-start justify-between gap-3 mb-2">
                                    <div>
                                        <div className="flex items-center gap-2 mb-1">
                                            <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded border ${change.type === 'create' ? 'bg-emerald-500/10 text-emerald-600 border-emerald-500/20' :
                                                change.type === 'update' ? 'bg-amber-500/10 text-amber-600 border-amber-500/20' :
                                                    'bg-red-500/10 text-red-600 border-red-500/20'
                                                }`}>
                                                {change.type.toUpperCase()}
                                            </span>
                                            <span className="text-xs font-mono font-bold text-cc-text truncate max-w-[200px]" title={change.sectionId}>
                                                {change.sectionId}
                                            </span>
                                        </div>
                                    </div>
                                </div>
                                <div className="pl-2 border-l-2 border-cc-border ml-1 mb-2">
                                    <p className="text-xs text-cc-text-subtle line-clamp-2 italic">
                                        "{change.content?.slice(0, 100)}..."
                                    </p>
                                </div>
                                {/* Change Actions */}
                                <div className="flex items-center justify-end gap-2 mt-2">
                                    <button
                                        onClick={() => {
                                            logger.info('Chat', 'Proposal Rejected (Single)', { messageId: msg.id, changeId: change.id });
                                            onRejectProposalChange(msg.id, change.id);
                                        }}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-cc-elevated border border-cc-border text-cc-text-muted hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-900/10 hover:border-red-200 transition-all text-xs font-medium"
                                    >
                                        <XCircle size={14} /> Reject
                                    </button>

                                    {/* Revision Logic */}
                                    <div className="relative">
                                        <button
                                            onClick={() => setActiveRevisionId(activeRevisionId === change.id ? null : change.id)}
                                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg border transition-all text-xs font-medium ${activeRevisionId === change.id
                                                ? 'bg-cc-accent-muted border-cc-accent text-cc-accent'
                                                : 'bg-cc-elevated border-cc-border text-cc-text-muted hover:text-cc-accent hover:border-cc-accent'
                                                }`}
                                        >
                                            <RefreshCw size={14} /> Revise
                                        </button>

                                        {/* Inline Revision Input */}
                                        {activeRevisionId === change.id && (
                                            <div className="absolute bottom-full right-0 mb-2 z-20 w-72 bg-cc-elevated shadow-xl rounded-xl border border-cc-border p-3 animate-in fade-in zoom-in-95 origin-bottom-right">
                                                <h5 className="text-[10px] font-bold uppercase text-cc-text-muted mb-2">Request Revision</h5>
                                                <textarea
                                                    autoFocus
                                                    className="w-full text-xs bg-cc-surface border border-cc-border rounded-lg p-2 mb-2 outline-none focus:ring-2 focus:ring-cc-accent text-cc-text resize-none"
                                                    rows={3}
                                                    placeholder="What would you like to change?"
                                                    value={revisionInputs[change.id] || ''}
                                                    onChange={(e) => setRevisionInputs((prev: any) => ({ ...prev, [change.id]: e.target.value }))}
                                                    onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSubmitRevision(msg.id, change.id); } }}
                                                />
                                                <div className="flex justify-end gap-2">
                                                    <button
                                                        onClick={() => setActiveRevisionId(null)}
                                                        className="px-2 py-1.5 text-[10px] text-cc-text-muted hover:bg-cc-tile-hover rounded-lg"
                                                    >
                                                        Cancel
                                                    </button>
                                                    <button
                                                        onClick={() => handleSubmitRevision(msg.id, change.id)}
                                                        className="px-3 py-1.5 text-[10px] bg-cc-accent text-cc-accent-text hover:bg-cc-accent-hover rounded-lg flex items-center gap-1 font-bold shadow-sm"
                                                    >
                                                        Send Request <ArrowRight size={10} />
                                                    </button>
                                                </div>
                                            </div>
                                        )}
                                    </div>

                                    <button
                                        onClick={() => onAcceptProposalChange(msg.id, change)}
                                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 shadow-md hover:shadow-lg transition-all text-xs font-bold"
                                    >
                                        <CheckCircle size={14} /> Accept
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
});

// Isolated Input Component to prevent re-rendering the whole chat on every keystroke
const ChatInput = React.memo(({
    onSendMessage,
    isProcessing,
    autoScrollEnabled,
    setAutoScrollEnabled,
    onStopProcessing,
    onStartLiveMode,
    onStartLocalVoiceMode,
    isLocalMode,
    isLiveMode,
    creativityLevel,
    setCreativityLevel
}: {
    onSendMessage: (text: string) => void;
    isProcessing: boolean;
    autoScrollEnabled: boolean;
    setAutoScrollEnabled: (enabled: boolean) => void;
    onStopProcessing?: () => void;
    onStartLiveMode?: () => void;
    onStartLocalVoiceMode?: () => void;
    isLocalMode?: boolean;
    isLiveMode?: boolean;
    creativityLevel?: number;
    setCreativityLevel?: (level: number) => void;
}) => {
    const [text, setText] = useState('');
    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const handleSend = () => {
        if (text.trim() && !isProcessing) {
            logger.info('Chat', 'Message Sent', { length: text.trim().length, isLocalMode, isLiveMode });
            onSendMessage(text.trim());
            setText('');
            // Reset height
            if (textareaRef.current) {
                textareaRef.current.style.height = 'auto';
            }
        }
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setText(e.target.value);
        // Auto-grow
        if (e.target) {
            e.target.style.height = 'auto';
            e.target.style.height = `${Math.min(e.target.scrollHeight, 200)}px`;
        }
    };

    return (
        <div className="p-4 bg-cc-surface border-t border-cc-border z-20 relative">
            <div className="max-w-4xl mx-auto relative flex items-end gap-2 bg-cc-elevated p-1.5 rounded-2xl border border-cc-border shadow-sm focus-within:ring-2 focus-within:ring-cc-accent/20 focus-within:border-cc-accent transition-all">

                {/* Scroll Toggle */}
                <button
                    onClick={() => setAutoScrollEnabled(!autoScrollEnabled)}
                    className={`p-2 rounded-xl transition-all mb-0.5 ${autoScrollEnabled
                        ? 'bg-cc-accent text-cc-accent-text shadow-sm'
                        : 'text-cc-text-muted hover:bg-cc-tile hover:text-cc-text'
                        }`}
                    title={autoScrollEnabled ? "Auto-scroll ON" : "Auto-scroll OFF"}
                >
                    <ArrowDownCircle size={18} />
                </button>

                <textarea
                    ref={textareaRef}
                    value={text}
                    onChange={handleChange}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message..."
                    className="flex-1 max-h-[200px] py-2.5 px-2 bg-transparent border-none focus:ring-0 resize-none text-sm sm:text-base text-cc-text placeholder-cc-text-muted custom-scrollbar"
                    rows={1}
                    style={{ minHeight: '44px' }}
                />

                <div className="flex items-center gap-1 mb-0.5">
                    {/* Local Voice Toggle (Browser/EdgeTTS) */}
                    {onStartLocalVoiceMode && (
                        <button
                            type="button"
                            onClick={onStartLocalVoiceMode ? () => onStartLocalVoiceMode() : undefined}
                            disabled={isProcessing || !onStartLocalVoiceMode}
                            className={`p-2 rounded-full transition-all duration-300 relative group/local ${isLocalMode
                                ? 'bg-cc-accent text-cc-accent-text shadow-sm ring-2 ring-cc-accent/50 animate-pulse'
                                : 'text-cc-text-muted hover:bg-cc-tile-hover hover:text-cc-accent'
                                }`}
                            title="Local Voice Chat (Browser)"
                        >
                            <Mic size={18} />
                            {isLocalMode && (
                                <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500"></span>
                                </span>
                            )}
                        </button>
                    )}

                    {/* Cloud Live Toggle (Gemini) */}
                    {onStartLiveMode && (
                        <button
                            type="button"
                            onClick={onStartLiveMode ? () => onStartLiveMode() : undefined}
                            disabled={isProcessing || !onStartLiveMode}
                            className={`p-2 rounded-full transition-all duration-300 relative group/live ${isLiveMode
                                ? 'bg-blue-500 text-white shadow-sm ring-2 ring-blue-500/50 animate-pulse'
                                : 'text-cc-text-muted hover:bg-cc-tile-hover hover:text-cc-accent'
                                }`}
                            title="Live Voice Chat (Cloud)"
                        >
                            <Sparkles size={18} />
                            {isLiveMode && (
                                <span className="absolute top-0 right-0 flex h-2.5 w-2.5">
                                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-blue-400 opacity-75"></span>
                                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-blue-500"></span>
                                </span>
                            )}
                        </button>
                    )}

                    {/* Creativity Slider Toggle */}
                    {setCreativityLevel && (
                        <div className="relative group/slider">
                            <button
                                className={`p-2 rounded-full transition-all duration-300 ${creativityLevel !== undefined && (creativityLevel < 0.3 || creativityLevel > 0.7)
                                    ? 'text-cc-accent bg-cc-accent/10'
                                    : 'text-cc-text-muted hover:bg-cc-tile-hover hover:text-cc-text'
                                    }`}
                                title={`Creativity: ${creativityLevel !== undefined ? (creativityLevel < 0.3 ? 'Analytical' : creativityLevel > 0.7 ? 'Creative' : 'Balanced') : 'Standard'}`}
                            >
                                <Settings2 size={18} />
                            </button>

                            {/* Hover/Focus Popup for Slider */}
                            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 w-48 p-3 bg-cc-elevated border border-cc-border rounded-xl shadow-xl opacity-0 invisible group-hover/slider:opacity-100 group-hover/slider:visible transition-all duration-200 z-50">
                                <div className="flex flex-col gap-2">
                                    <div className="flex justify-between items-center text-[10px] text-cc-text-muted font-medium uppercase tracking-wider">
                                        <span>Analytical</span>
                                        <span>Creative</span>
                                    </div>
                                    <input
                                        type="range"
                                        min="0"
                                        max="1"
                                        step="0.1"
                                        value={creativityLevel ?? 0.5}
                                        onChange={(e) => setCreativityLevel(parseFloat(e.target.value))}
                                        className="w-full h-1.5 bg-cc-surface rounded-full appearance-none cursor-pointer accent-cc-accent"
                                    />
                                    <div className="text-center text-xs font-medium text-cc-accent">
                                        {creativityLevel !== undefined ? (creativityLevel < 0.3 ? 'Strict Analysis' : creativityLevel > 0.7 ? 'High Creativity' : 'Balanced') : 'Standard'}
                                    </div>
                                </div>
                                <div className="absolute -bottom-1.5 left-1/2 -translate-x-1/2 w-3 h-3 bg-cc-elevated border-b border-r border-cc-border rotate-45 transform"></div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Send / Stop Button */}
                {isProcessing ? (
                    <button
                        onClick={onStopProcessing}
                        className="p-2 rounded-xl bg-cc-tile hover:bg-red-500/10 text-cc-text-muted hover:text-red-500 transition-colors"
                        title="Stop generating"
                    >
                        <Loader2 size={18} className="animate-spin" />
                    </button>
                ) : (
                    <button
                        onClick={handleSend}
                        disabled={!text.trim()}
                        className={`p-2 rounded-xl transition-all ${text.trim()
                            ? 'bg-cc-accent text-cc-accent-text shadow-md hover:scale-105 active:scale-95'
                            : 'bg-cc-tile text-cc-text-muted cursor-not-allowed'
                            }`}
                    >
                        <Send size={18} />
                    </button>
                )}
            </div>

            <div className="absolute top-0 left-0 right-0 h-4 -translate-y-full bg-gradient-to-t from-cc-base to-transparent pointer-events-none" />
        </div>
    );
});

export const ChatInterface: React.FC<ChatInterfaceProps> = React.memo(({
    messages,
    activeIdea,
    currentPlan,
    onSendMessage,
    onAcceptProposalChange,
    onRejectProposalChange,
    onDeleteMessage,
    onRequestRevision,
    onAcceptAllChanges,
    onRejectAllChanges,
    isProcessing,
    onStartLiveMode,
    isLocalMode = false,
    onStartLocalVoiceMode,
    isArchitectMode = false,
    setIsArchitectMode,
    creativityLevel,
    setCreativityLevel,
    hideHeader = false,
    isLiveMode = false,
    liveStatus = 'idle',
    isMicOn = true,
    setIsMicOn,
    isAudioSkipped = false,
    isAudioPlaying = false,
    isWaitingForResponse = false,
    streamingModelText = '',
    selectedAvatar = 'nexus',
    onToggleAudioSkip,
    showControlsBar: externalShowControlsBar,
    setShowControlsBar: externalSetShowControlsBar,
    isChatFullscreen = false,
    setIsChatFullscreen,
    isChatCollapsed = false,
    setIsChatCollapsed,
    showCompactSearch = false,
    modelContextLength = 131072, // Default to 128k
    theme = 'light',
    activeProvider,
    onToggleProvider
}) => {
    const ActiveIcon = AVATAR_STYLES.find(s => s.id === selectedAvatar)?.icon;
    const [searchTerm, setSearchTerm] = useState('');
    const messagesEndRef = useRef<HTMLDivElement>(null);
    const planEndRef = useRef<HTMLDivElement>(null);
    const [autoScrollEnabled, setAutoScrollEnabled] = useState(false);

    // Toggle for showing controls bar vs tagline (in live mode)
    // Use external state if provided, otherwise use internal state (default to false = show floating HUD)
    const [internalShowControlsBar, setInternalShowControlsBar] = useState(false);
    const showControlsBar = externalShowControlsBar !== undefined ? externalShowControlsBar : internalShowControlsBar;
    const setShowControlsBar = externalSetShowControlsBar || setInternalShowControlsBar;

    // State for revision inputs: Map of changeId -> text
    const [revisionInputs, setRevisionInputs] = useState<Record<string, string>>({});
    const [activeRevisionId, setActiveRevisionId] = useState<string | null>(null);

    // Master Plan sidebar collapse state - auto-collapse on mobile (<768px)
    const [isPlanCollapsed, setIsPlanCollapsed] = useState(() => {
        if (typeof window !== 'undefined') {
            return window.innerWidth < 768;
        }
        return false;
    });

    // Auto-collapse on resize to mobile
    useEffect(() => {
        const handleResize = () => {
            if (window.innerWidth < 768) {
                setIsPlanCollapsed(true);
            }
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Native scroll is much more performant than JS-based physics loops for large lists
    const messagesContainerRef = useRef<HTMLDivElement>(null);

    // Auto-scroll to bottom
    // Auto-scroll to bottom
    useEffect(() => {
        if (autoScrollEnabled) {
            messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, autoScrollEnabled]);

    // Auto-scroll plan
    useEffect(() => {
        if (currentPlan) {
            planEndRef.current?.scrollIntoView({ behavior: 'smooth' });
        }
    }, [currentPlan]);

    // Note: handleSend and handleKeyDown are now in ChatInput component to prevent typing re-renders

    // Handler for proposal revision text inputs
    const handleSubmitRevision = (msgId: string, changeId: string) => {
        const feedback = revisionInputs[changeId];
        if (feedback?.trim()) {
            onRequestRevision(msgId, changeId, feedback);
            setActiveRevisionId(null);
            setRevisionInputs(prev => ({ ...prev, [changeId]: '' }));
            logger.info('Chat', 'Revision Requested', { messageId: msgId, changeId, feedback });
        }
    };



    // Filter messages based on active idea and search term - memoized for performance
    const filteredMessages = useMemo(() => {
        return messages.filter(msg => {
            if (activeIdea) {
                if (msg.ideaId !== activeIdea.id) return false;
            } else {
                if (msg.ideaId !== null) return false;
            }

            if (searchTerm) {
                return msg.text.toLowerCase().includes(searchTerm.toLowerCase());
            }
            return true;
        });
    }, [messages, activeIdea, searchTerm]);

    if (isChatCollapsed && setIsChatCollapsed) {
        return (
            <div className="h-full bg-cc-elevated border-t border-cc-border flex items-center justify-between px-4">
                <button
                    onClick={() => setIsChatCollapsed(false)}
                    className="flex items-center gap-2 text-cc-text-muted hover:text-cc-accent transition-colors"
                >
                    <MessageSquare size={16} />
                    <span className="text-xs font-medium">Show Chat</span>
                    <ChevronUp size={14} />
                </button>
                <div className="flex items-center gap-2">
                    <span className="text-[10px] text-cc-text-subtle">
                        {messages.length} message{messages.length !== 1 ? 's' : ''}
                    </span>
                    <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold uppercase ${isArchitectMode
                        ? 'bg-cc-accent/10 text-cc-accent'
                        : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
                        }`}>
                        {isArchitectMode ? 'Architect' : 'Explorer'}
                    </span>
                </div>
            </div>
        );
    }

    return (
        <div className="flex h-full w-full bg-cc-base font-sans transition-colors duration-300 overflow-hidden">

            {/* LEFT: MAIN CHAT */}
            <div className="flex-1 flex flex-col relative overflow-hidden">

                {/* Header Area (Absolute) - Only show if not hidden */}
                {!hideHeader && (
                    <div className="absolute top-4 right-8 z-30 flex items-center gap-3 animate-in fade-in slide-in-from-top-2">
                        {/* Provider Toggle */}
                        {onToggleProvider && (
                            <button
                                onClick={onToggleProvider}
                                className={`h-9 px-3 backdrop-blur-sm border rounded-full transition-all shadow-sm flex items-center gap-2 ${activeProvider === 'onnx'
                                    ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/30 hover:bg-emerald-500/20'
                                    : 'bg-cc-elevated/80 text-cc-text-muted border-cc-border hover:text-cc-accent hover:bg-cc-tile'
                                    }`}
                                title={`Current: ${activeProvider === 'onnx' ? 'AI Edge (ONNX)' : 'Main Provider'}. Click to switch.`}
                            >
                                {activeProvider === 'onnx' ? <Zap size={14} className="fill-current" /> : <Cloud size={14} />}
                                <span className="text-[10px] font-bold uppercase tracking-wider hidden sm:inline">
                                    {activeProvider === 'onnx' ? 'Edge' : 'Cloud'}
                                </span>
                            </button>
                        )}

                        {/* Collapse Chat Toggle */}
                        {setIsChatCollapsed && !isChatFullscreen && (
                            <button
                                onClick={() => setIsChatCollapsed(true)}
                                className="p-2 bg-cc-elevated/80 backdrop-blur-sm border border-cc-border rounded-full text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile transition-all shadow-sm"
                                title="Collapse chat"
                            >
                                <ChevronDown size={14} />
                            </button>
                        )}
                        {/* Fullscreen Toggle - Mobile/Tablet only */}
                        {setIsChatFullscreen && (
                            <button
                                onClick={() => setIsChatFullscreen(!isChatFullscreen)}
                                className="lg:hidden p-2 bg-cc-elevated/80 backdrop-blur-sm border border-cc-border rounded-full text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile transition-all shadow-sm"
                                title={isChatFullscreen ? "Exit fullscreen" : "Fullscreen chat"}
                            >
                                {isChatFullscreen ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
                            </button>
                        )}
                        {/* Search Bar */}
                        <div className="relative group w-48 transition-all focus-within:w-64">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-cc-text-muted group-focus-within:text-cc-accent w-3.5 h-3.5" />
                            <input
                                type="text"
                                placeholder="Search history..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-9 pr-4 py-1.5 bg-cc-elevated/80 backdrop-blur-sm border border-cc-border rounded-full text-xs shadow-sm focus:ring-2 focus:ring-cc-accent focus:outline-none transition-all text-cc-text placeholder-cc-text-muted"
                            />
                        </div>
                    </div>
                )}

                {/* Compact Search (Sidebar Mode) */}
                {hideHeader && showCompactSearch && (
                    <div className="absolute top-2 right-4 z-30 animate-in fade-in slide-in-from-top-2">
                        <div className="relative group w-full max-w-[180px] transition-all focus-within:max-w-[220px]">
                            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 text-cc-text-muted group-focus-within:text-cc-accent w-3 h-3" />
                            <input
                                type="text"
                                placeholder="Search..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-8 pr-3 py-1.5 bg-cc-elevated/90 backdrop-blur-sm border border-cc-border rounded-lg text-[11px] shadow-sm focus:ring-1 focus:ring-cc-accent focus:outline-none transition-all text-cc-text placeholder-cc-text-muted"
                            />
                            {searchTerm && (
                                <button
                                    onClick={() => setSearchTerm('')}
                                    className="absolute right-2 top-1/2 -translate-y-1/2 text-cc-text-muted hover:text-cc-text"
                                >
                                    <X size={10} />
                                </button>
                            )}
                        </div>
                    </div>
                )}

                {/* Messages List - Native Scroll */}
                <div
                    ref={messagesContainerRef}
                    className={`flex-1 overflow-y-auto p-4 sm:p-8 space-y-8 chat-scroll-container custom-scrollbar ${!hideHeader ? 'pt-20' : ''} pb-4 performance-container gpu-accelerated`}
                    style={{ scrollBehavior: 'smooth' }}
                >
                    {filteredMessages.length === 0 && (
                        <div className="flex flex-col items-center justify-center h-full text-cc-text-muted select-none animate-in fade-in duration-700">
                            {isArchitectMode ? (
                                activeIdea ? (
                                    <div className="text-center max-w-md">
                                        <div className="w-20 h-20 bg-cc-accent rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-2xl">
                                            <Component size={40} className="text-cc-accent-text" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-cc-text mb-2">The Architect Engine</h2>
                                        <p className="text-sm text-cc-text-muted mb-6">
                                            Symbiotic Concept Construction Active. <br />
                                            Ready to transform "{activeIdea.title}" into reality.
                                        </p>
                                        <div className="flex justify-center gap-2">
                                            <span className="px-3 py-1 rounded-full bg-cc-accent-muted text-[10px] font-bold uppercase text-cc-accent border border-cc-accent/20">
                                                Thinking Enabled
                                            </span>
                                            <span className="px-3 py-1 rounded-full bg-cc-accent-muted text-[10px] font-bold uppercase text-cc-accent border border-cc-accent/20">
                                                Proposals Active
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="text-center max-w-md">
                                        <div className="w-20 h-20 bg-cc-accent rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl">
                                            <BrainCircuit size={40} className="text-cc-accent-text" />
                                        </div>
                                        <h2 className="text-2xl font-bold text-cc-text mb-2">The Architect Engine</h2>
                                        <p className="text-sm text-cc-text-muted">
                                            Systems Initialized. <br />
                                            Start a new project or explore ideas episodically.
                                        </p>
                                    </div>
                                )
                            ) : (
                                <div className="text-center max-w-md">
                                    <div className="w-20 h-20 bg-cc-accent rounded-2xl mx-auto mb-6 flex items-center justify-center shadow-xl">
                                        <Search size={40} className="text-cc-accent-text" />
                                    </div>
                                    <h2 className="text-2xl font-bold text-cc-text mb-6">Teaching Explorations</h2>
                                    <div className="flex justify-center gap-2 flex-wrap">
                                        <span className="px-3 py-1 rounded-full bg-cc-accent-muted text-[10px] font-bold uppercase text-cc-accent border border-cc-accent/20">
                                            Web Grounded
                                        </span>
                                        <span className="px-3 py-1 rounded-full bg-cc-accent-muted text-[10px] font-bold uppercase text-cc-accent border border-cc-accent/20">
                                            Source Analysis
                                        </span>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {filteredMessages.map((msg) => {
                        // Special rendering for Briefing messages (voice-delegated search)
                        if (msg.isBriefing) {
                            return (
                                <div key={msg.id} className="flex flex-col w-full items-start">
                                    <div className="flex gap-3 max-w-5xl w-full">
                                        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 mt-1 shadow-sm bg-cc-accent text-cc-accent-text">
                                            <FileText size={16} />
                                        </div>
                                        <div className="flex-1">
                                            <BriefingCard
                                                query={msg.briefingQuery || 'Research query'}
                                                content={msg.text}
                                                sources={msg.groundingSources || []}
                                            />
                                        </div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <ChatMessage
                                key={msg.id}
                                msg={msg}
                                isLiveMode={isLiveMode}
                                activeIdea={activeIdea}
                                activeRevisionId={activeRevisionId}
                                revisionInputs={revisionInputs}
                                setActiveRevisionId={setActiveRevisionId}
                                setRevisionInputs={setRevisionInputs}
                                handleSubmitRevision={handleSubmitRevision}
                                onRejectProposalChange={onRejectProposalChange}
                                onAcceptProposalChange={onAcceptProposalChange}
                                onAcceptAllChanges={onAcceptAllChanges}
                                onRejectAllChanges={onRejectAllChanges}
                                onSendMessage={onSendMessage}
                                onDeleteMessage={onDeleteMessage}
                                theme={theme}
                            />
                        );
                    })}
                    <div ref={messagesEndRef} />
                </div >

                {/* Input Area - Flex Item (Static Bottom) */}
                {/* Input Area - Now Isolated */}
                <ChatInput
                    onSendMessage={onSendMessage}
                    isProcessing={isProcessing}
                    autoScrollEnabled={autoScrollEnabled}
                    setAutoScrollEnabled={setAutoScrollEnabled}
                    onStopProcessing={isProcessing ? () => { /* Add stop logic if available */ } : undefined}
                    onStartLiveMode={onStartLiveMode}
                    onStartLocalVoiceMode={onStartLocalVoiceMode}
                    isLocalMode={isLocalMode}
                    isLiveMode={isLiveMode}
                    creativityLevel={creativityLevel}
                    setCreativityLevel={setCreativityLevel}
                />
                {/* Bottom Bar: Either tagline OR controls bar (toggle in live mode) */}
                <div className="max-w-4xl mx-auto mt-2">
                    {isLiveMode && showControlsBar ? (
                        /* Live Mode Controls Bar - Same width as input field */
                        <div className="flex items-center justify-between px-4 py-2 bg-cc-surface border border-cc-border rounded-2xl">
                            {/* Left: Mini Avatar with Speech Animation */}
                            {ActiveIcon && (
                                <div
                                    className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-all relative ${isAudioPlaying
                                        ? 'bg-cc-accent text-cc-accent-text avatar-speaking'
                                        : isAudioSkipped
                                            ? 'bg-cc-surface text-cc-text-muted border border-cc-border opacity-60'
                                            : 'bg-cc-surface text-cc-accent border border-cc-border'
                                        }`}
                                    title={`${AVATAR_STYLES.find(s => s.id === selectedAvatar)?.name || selectedAvatar}${isAudioPlaying ? ' (speaking...)' : isAudioSkipped ? ' (voice skipped)' : ''}`}
                                >
                                    <ActiveIcon size={16} className={isAudioPlaying ? 'avatar-speaking-icon' : ''} />
                                    {isAudioSkipped && (
                                        <div className="absolute inset-0 flex items-center justify-center bg-cc-surface/70 rounded-full">
                                            <SkipForward size={10} className="text-cc-text-muted" />
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Center: Processing Indicator */}
                            <div className="flex-1 mx-3 flex items-center justify-center">
                                <div className={`flex items-center gap-2 px-3 py-1 rounded-full bg-cc-tile ${isAudioSkipped ? 'border border-amber-500/30' : ''}`}>
                                    <Loader2 size={12} className={`animate-spin shrink-0 ${isAudioSkipped ? 'text-amber-500' : 'text-cc-accent'}`} />
                                    <span className="text-[10px] text-cc-text-muted font-medium whitespace-nowrap">
                                        {isAudioPlaying
                                            ? 'Speaking'
                                            : isAudioSkipped && streamingModelText
                                                ? 'Reading (voice off)'
                                                : streamingModelText
                                                    ? 'Preparing voice'
                                                    : isWaitingForResponse
                                                        ? 'Thinking'
                                                        : 'Listening'}
                                    </span>
                                    <span className="flex gap-0.5">
                                        <span className={`w-1 h-1 rounded-full ${isAudioSkipped ? 'bg-amber-500' : 'bg-cc-accent'} animate-pulse`}></span>
                                        <span className={`w-1 h-1 rounded-full ${isAudioSkipped ? 'bg-amber-500' : 'bg-cc-accent'} animate-pulse delay-75`}></span>
                                        <span className={`w-1 h-1 rounded-full ${isAudioSkipped ? 'bg-amber-500' : 'bg-cc-accent'} animate-pulse delay-150`}></span>
                                    </span>
                                </div>
                            </div>

                            {/* Right: Skip + Mic Controls */}
                            <div className="flex items-center gap-2">
                                {/* Skip Voice Button */}
                                <button
                                    onClick={onToggleAudioSkip}
                                    className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${isAudioSkipped
                                        ? 'bg-amber-500 text-white'
                                        : 'bg-cc-elevated text-cc-text-muted border border-cc-border hover:text-cc-accent hover:border-cc-accent'
                                        }`}
                                    title={isAudioSkipped ? "Resume AI voice" : "Skip AI voice (text continues)"}
                                >
                                    {isAudioSkipped ? <Play size={14} /> : <SkipForward size={14} />}
                                </button>

                                {/* Mic Button */}
                                <button
                                    onClick={() => setIsMicOn?.(!isMicOn)}
                                    className={`w-8 h-8 rounded-full shadow-md flex items-center justify-center transition-all hover:scale-110 active:scale-95 relative ${isMicOn
                                        ? 'bg-cc-accent text-cc-accent-text'
                                        : 'bg-red-500/20 text-red-500 border border-red-500/50'
                                        }`}
                                    title={isMicOn ? "Mute microphone" : "Unmute microphone"}
                                >
                                    {isMicOn ? <Mic size={16} /> : <MicOff size={16} />}
                                </button>
                            </div>

                            {/* Toggle to tagline */}
                            <button
                                onClick={() => setShowControlsBar(false)}
                                className="ml-2 p-1.5 text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile rounded-lg transition-all"
                                title="Show tagline"
                            >
                                <Settings2 size={14} />
                            </button>
                        </div>
                    ) : (
                        /* Tagline (default view) - Hidden to reduce gap */
                        <div className="relative h-0">
                            {/* Toggle button to show controls bar (only in live mode) */}
                            {isLiveMode && (
                                <button
                                    onClick={() => setShowControlsBar(true)}
                                    className="absolute right-0 bottom-1 p-1.5 text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile rounded-lg transition-all"
                                    title="Show live controls"
                                >
                                    <Settings2 size={14} />
                                </button>
                            )}
                        </div>
                    )}
                </div>
            </div >

            {/* RIGHT: ARCHITECTURAL PLAN SIDEBAR - Show whenever a plan exists (active or completed) */}
            {
                currentPlan && isArchitectMode && (
                    <>
                        {/* Collapsed toggle button (right edge) */}
                        {isPlanCollapsed && (
                            <button
                                onClick={() => setIsPlanCollapsed(false)}
                                className="fixed right-0 top-1/2 -translate-y-1/2 z-40 bg-cc-accent text-cc-accent-text p-2 rounded-l-lg shadow-lg hover:bg-cc-accent-hover transition-all animate-in slide-in-from-right duration-300"
                                title="Show Master Plan"
                            >
                                <div className="flex flex-col items-center gap-1">
                                    <ChevronLeft size={16} />
                                    <ListChecks size={16} />
                                </div>
                            </button>
                        )}

                        {/* Expanded sidebar */}
                        {!isPlanCollapsed && (
                            <div className="w-72 sm:w-80 max-w-[85vw] bg-cc-elevated border-l border-cc-border flex flex-col shadow-2xl animate-in slide-in-from-right-10 duration-300 z-30 fixed md:relative right-0 top-12 md:top-0 h-[calc(100%-3rem)] md:h-auto">
                                <div className="p-3 sm:p-4 border-b border-cc-border bg-cc-surface">
                                    <div className="flex items-center justify-between gap-2 mb-1">
                                        <div className="flex items-center gap-2 text-cc-accent min-w-0">
                                            <ListChecks size={18} className="flex-shrink-0" />
                                            <h3 className="text-xs sm:text-sm font-bold uppercase tracking-wider truncate">Master Plan</h3>
                                        </div>
                                        <button
                                            onClick={() => setIsPlanCollapsed(true)}
                                            className="p-1.5 text-cc-text-muted hover:text-cc-accent hover:bg-cc-tile rounded-lg transition-all flex-shrink-0"
                                            title="Collapse sidebar"
                                        >
                                            <ChevronRight size={16} />
                                        </button>
                                    </div>
                                    <h2 className="text-base sm:text-lg font-bold text-cc-text leading-tight break-words">
                                        {currentPlan.title}
                                    </h2>
                                </div>

                                <div className="flex-1 overflow-y-auto p-3 sm:p-4 space-y-3 sm:space-y-4 custom-scrollbar">
                                    {currentPlan.steps.map((step, idx) => (
                                        <div key={step.id} className="flex gap-2 sm:gap-3 relative">
                                            {/* Connector Line */}
                                            {idx < currentPlan.steps.length - 1 && (
                                                <div className="absolute left-[9px] top-6 bottom-[-12px] sm:bottom-[-16px] w-[2px] bg-cc-border" />
                                            )}

                                            {/* Status Indicator */}
                                            <div className={`mt-0.5 flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center border-2 ${step.status === 'completed'
                                                ? 'bg-emerald-500 border-emerald-500 text-white'
                                                : step.status === 'in_progress'
                                                    ? 'bg-cc-accent-muted border-cc-accent text-cc-accent animate-pulse'
                                                    : 'bg-transparent border-cc-border text-transparent'
                                                }`}>
                                                {step.status === 'completed' && <Check size={12} strokeWidth={3} />}
                                                {step.status === 'in_progress' && <div className="w-2 h-2 rounded-full bg-cc-accent" />}
                                            </div>

                                            <div className={`flex-1 min-w-0 ${step.status === 'completed' ? 'opacity-50 grayscale' : 'opacity-100'} transition-all`}>
                                                <p className="text-xs sm:text-sm text-cc-text font-medium leading-snug break-words">
                                                    {step.description}
                                                </p>
                                                {step.status === 'in_progress' && (
                                                    <span className="text-[10px] text-cc-accent font-bold uppercase tracking-wider mt-1 block">
                                                        Constructing...
                                                    </span>
                                                )}
                                            </div>
                                        </div>
                                    ))}
                                    <div ref={planEndRef} />
                                </div>

                                <div className={`p-2 sm:p-3 bg-cc-surface border-t border-cc-border text-[10px] text-center ${currentPlan.isActive ? 'text-cc-accent' : 'text-cc-text-muted'}`}>
                                    {currentPlan.isActive ? 'Execution Status: Active' : 'Plan Available • Say "Continue" to resume'}
                                </div>
                            </div>
                        )}

                        {/* Backdrop overlay for mobile when sidebar is open */}
                        {!isPlanCollapsed && (
                            <div
                                className="fixed inset-0 bg-black/30 z-20 md:hidden"
                                onClick={() => setIsPlanCollapsed(true)}
                            />
                        )}
                    </>
                )
            }

        </div >
    );
});